import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";

import { AuthService } from "@core/services/auth.service";
import { ButtonWithLoadingComponent } from "@shared/components/button-with-loading/button-with-loading.component";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { InputComponent } from "@shared/components/input/input.component";
import { BaseModalComponent } from "@shared/components/modal/base-modal.component";
import { ModalComponent } from "@shared/components/modal/modal.component";
import { ModalService } from "@shared/components/modal/modal.service";
import { EMAIL_PATTERN, PASSWORD_PATTERN } from "@shared/validators/custom-validators";

type ResetStep = "email" | "sent" | "reset" | "success";

@Component({
  selector: "component-password-reset-modal",
  templateUrl: "./password-reset-modal.component.html",
  imports: [ModalComponent, ButtonWithLoadingComponent, CustomIconComponent, InputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasswordResetModalComponent extends BaseModalComponent implements OnInit {
  private readonly _authService = inject(AuthService);
  private readonly _modalService = inject(ModalService);

  // Step management
  protected readonly currentStep = signal<ResetStep>("email");
  protected readonly isEmailStep = computed(() => this.currentStep() === "email");
  protected readonly isSentStep = computed(() => this.currentStep() === "sent");
  protected readonly isResetStep = computed(() => this.currentStep() === "reset");
  protected readonly isSuccessStep = computed(() => this.currentStep() === "success");

  // Form state
  protected readonly email = signal("");
  protected readonly password = signal("");
  protected readonly emailError = signal<string | null>(null);
  protected readonly passwordError = signal<string | null>(null);
  protected readonly isLoading = signal(false);
  protected readonly passwordTouched = signal(false);

  // Reset token from URL params (will be set when modal opens for reset step)
  protected readonly resetToken = signal<string | null>(null);

  // Context flag: true if opened from profile settings OR if user is authenticated (logged in)
  protected readonly isFromProfile = signal(false);
  protected readonly isUserAuthenticated = computed(() => !!this._authService.currentUser());
  protected readonly isEmailReadonly = computed(() => this.isFromProfile() && this.isResetStep());

  // Focus management
  protected readonly shouldFocusEmail = signal(false);
  protected readonly shouldFocusPassword = signal(false);

  ngOnInit(): void {
    // Trigger focus after modal is fully rendered
    setTimeout(() => {
      if (this.isEmailStep()) {
        this.shouldFocusEmail.set(true);
      } else if (this.isResetStep()) {
        this.shouldFocusPassword.set(true);
      }
    }, 100);
  }

  // Initialize from profile settings (change password)
  initializeFromProfile(email: string): void {
    this.email.set(email);
    this.isFromProfile.set(true);
    this.currentStep.set("email"); // Start at email step to request reset
  }

  // Initialize with reset token if provided
  initializeWithToken(token: string, email: string): void {
    this.resetToken.set(token);
    this.email.set(email);
    this.currentStep.set("reset");
  }

  protected onEmailChange(value: string): void {
    this.email.set(value);
    this.emailError.set(null);
  }

  protected onEmailEnter(): void {
    if (this.isEmailStep()) {
      this.onRequestReset();
    }
  }

  protected onPasswordChange(value: string): void {
    this.password.set(value);

    if (this.passwordTouched()) {
      this.validatePassword();
    }
  }

  protected onPasswordEnter(): void {
    if (this.isResetStep()) {
      this.onResetPassword();
    }
  }

  private validatePassword(): string | null {
    if (!this.password()) {
      const error = "Password is required";
      this.passwordError.set(error);
      return error;
    }

    if (this.password().length < 8) {
      const error = "Password must be at least 8 characters";
      this.passwordError.set(error);
      return error;
    }

    if (!PASSWORD_PATTERN.test(this.password())) {
      const error =
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character";
      this.passwordError.set(error);
      return error;
    }

    this.passwordError.set(null);
    return null;
  }

  protected onRequestReset(): void {
    if (!this.email() || !EMAIL_PATTERN.test(this.email())) {
      this.emailError.set("Please enter a valid email address");
      return;
    }

    this.emailError.set(null);
    this.isLoading.set(true);

    this._authService.requestPasswordReset(this.email()).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.currentStep.set("sent");
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.emailError.set((err.error as any)?.message || "Failed to send reset email");
      }
    });
  }

  protected onResetPassword(): void {
    this.passwordTouched.set(true);

    const error = this.validatePassword();
    if (error) {
      return;
    }

    if (!this.resetToken()) {
      this.passwordError.set("Invalid reset token");
      return;
    }

    this.isLoading.set(true);

    this._authService.resetPassword(this.resetToken()!, this.email(), this.password()).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.currentStep.set("success");
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.passwordError.set((err.error as any)?.message || "Failed to reset password");
      }
    });
  }

  protected onResendInstructions(): void {
    this.currentStep.set("email");
  }

  protected onBackClick(): void {
    if (this.isEmailStep()) {
      this.onBackToLogin();
    } else if (this.isSentStep()) {
      this.onBackToEmail();
    }
  }

  protected onBackToLogin(): void {
    this.activeModal.close();

    // If this was from profile OR user is authenticated, just close the modal
    if (this.isFromProfile() || this.isUserAuthenticated()) {
      return;
    }

    // Otherwise, open login modal with prefilled email
    const modalRef = this._modalService.openLoginModal();
    if (modalRef.componentInstance && this.email()) {
      modalRef.componentInstance.email.set(this.email());
    }
  }

  protected onBackToEmail(): void {
    this.currentStep.set("email");
    this.password.set("");
    this.passwordError.set(null);
    this.passwordTouched.set(false);

    // Focus email input when going back to email step
    setTimeout(() => {
      this.shouldFocusEmail.set(false);
      this.shouldFocusEmail.set(true);
    }, 0);
  }
}
